'use client';

import {
  IconButton,
  Input,
  MetaDefault,
  toasterApi,
} from '@terra/ui-design-system';
import { Box } from '@terra/ui-styled-system';
import {
  ArtificialIntelligence08Icon,
  Loading03Icon,
  Edit03Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import type { KeyboardEvent } from 'react';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { ChatTabContextMenu } from './ChatTabContextMenu';
import { useChatInstancesStore } from '../../../store/chatInstancesStore';
import type { ChatTabType } from '../../../store/chatTabsStore';
import { useChatTabs } from '../../../store/chatTabsStore';
import { useConversations } from '../../../store/conversations-store';
import { switchToChatTab } from '../actions/chatActions';
import { useUnreadTabsStore } from '../../../store/unreadTabsStore';
import { useChatTabDisplayTitle } from '../../../hooks/useChatTabDisplayTitle';
import { hasDraftChatTabContent } from '../chatTabDisplayTitle';

type ChatTabProps = {
  tab: ChatTabType;
  index: number;
  contentContainerRef: React.RefObject<HTMLDivElement | null>;
};

export function ChatTab({ tab, index, contentContainerRef }: ChatTabProps) {
  const {
    updateChatTab: onUpdateTitle,
    closeTab: onCloseTab,
    activeChatTabId,
    chatTabs,
  } = useChatTabs();
  const { updateConversation } = useConversations({
    targetId: tab.targetId,
  });
  const tCopilot = useTranslations('chat.copilot');
  const totalTabs = chatTabs.length;
  const [isEditing, setIsEditing] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  // Subscribe to the chat status directly so component re-renders when status changes
  const chatStatus = useChatInstancesStore(
    (state) =>
      state.chatStatuses.get(tab.chatId) ??
      state.chats.get(tab.chatId)?.status ??
      null,
  );
  const isUnread = useUnreadTabsStore((state) =>
    state.unreadTabIds.has(tab.tabId),
  );
  const isActive = activeChatTabId === tab.tabId;
  const displayTitle = useChatTabDisplayTitle(tab);

  useChatInstancesStore((state) => {
    const chat = state.chats.get(tab.chatId);
    const queuedCount = state.getQueuedMessages({ chatId: tab.chatId }).length;

    return `${tab.draftInput ?? ''}:${chat?.messages.length ?? 0}:${queuedCount}`;
  });
  const showDraftIcon = hasDraftChatTabContent(tab);
  const tabRef = useRef<HTMLDivElement>(null);
  const isEditingRef = useRef(false);

  useEffect(() => {
    if (isActive && tabRef.current) {
      requestAnimationFrame(() => {
        tabRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      });
    }
  }, [isActive]);
  // Calculate dynamic width based on number of tabs
  const maxWidth =
    totalTabs <= 3
      ? '120px'
      : totalTabs <= 5
        ? '100px'
        : totalTabs <= 7
          ? '80px'
          : '60px';
  const isChatActive = chatStatus === 'streaming' || chatStatus === 'submitted';
  const handleStartEdit = useCallback((currentTitle: string) => {
    isEditingRef.current = true;
    setIsEditing(true);
    setEditingTitle(currentTitle);
  }, []);
  const handleSaveEdit = useCallback(() => {
    if (!isEditingRef.current) {
      return;
    }

    isEditingRef.current = false;

    const nextTitle = editingTitle.trim();
    const previousTitle = tab.title;
    const previousHasCustomTitle = tab.hasCustomTitle;

    if (!nextTitle) {
      setIsEditing(false);
      setEditingTitle('');

      return;
    }

    if (nextTitle === previousTitle) {
      setIsEditing(false);
      setEditingTitle('');

      return;
    }

    onUpdateTitle(tab.tabId, {
      title: nextTitle,
      hasCustomTitle: true,
    });
    setIsEditing(false);
    setEditingTitle('');

    if (!tab.conversationId) {
      return;
    }

    void updateConversation({
      conversationId: tab.conversationId,
      title: nextTitle,
    }).catch((error: unknown) => {
      onUpdateTitle(tab.tabId, {
        title: previousTitle,
        hasCustomTitle: previousHasCustomTitle,
      });
      toasterApi.error({
        title: tCopilot('renameConversationFailed'),
        description:
          error instanceof Error && error.message ? error.message : undefined,
      });
    });
  }, [
    editingTitle,
    onUpdateTitle,
    tCopilot,
    tab.conversationId,
    tab.hasCustomTitle,
    tab.tabId,
    tab.title,
    updateConversation,
  ]);
  const handleCancelEdit = useCallback(() => {
    isEditingRef.current = false;
    setIsEditing(false);
    setEditingTitle('');
  }, []);
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveEdit();
      } else if (e.key === 'Escape') {
        handleCancelEdit();
      }
    },
    [handleSaveEdit, handleCancelEdit],
  );

  return (
    <ChatTabContextMenu
      key={tab.tabId}
      tabId={tab.tabId}
      threadId={tab.chatId}
      contentContainerRef={contentContainerRef}
    >
      <Box
        ref={tabRef}
        data-tab-id={tab.tabId}
        position="relative"
        onClick={() => !isEditing && switchToChatTab({ tabId: tab.tabId })}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        px="2"
        py="1"
        borderRadius="l2"
        bg={isActive ? 'bg.muted' : 'transparent'}
        cursor="pointer"
        display="flex"
        alignItems="center"
        gap="1"
        minW="fit-content"
        maxW={maxWidth}
        flexShrink={index === 0 ? 0 : 1}
        transition="background 0.1s"
        _hover={{
          bg: 'bg.muted',
        }}
      >
        {isEditing ? (
          <Input
            value={editingTitle}
            onChange={(e) => setEditingTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSaveEdit}
            size="xs"
            flex="1"
            minW="0"
            autoFocus
            onClick={(e) => e.stopPropagation()}
            fontSize="xs"
            h="auto"
            py="0"
            px="1"
          />
        ) : (
          <>
            {isUnread && !isActive && (
              <Box
                position="absolute"
                top="2px"
                right="2px"
                w="5px"
                h="5px"
                borderRadius="full"
                bg="blue.9"
                pointerEvents="none"
              />
            )}
            {isChatActive ? (
              <Box
                as="span"
                display="inline-flex"
                flexShrink={0}
                animationStyle="spin"
                color="terra.a9"
              >
                <Loading03Icon size={12} color="currentColor" />
              </Box>
            ) : showDraftIcon ? (
              <Box
                as="span"
                display="inline-flex"
                flexShrink={0}
                color="fg.muted"
              >
                <Edit03Icon size={12} />
              </Box>
            ) : (
              <Box
                as="span"
                display="inline-flex"
                flexShrink={0}
                color="fg.muted"
              >
                <ArtificialIntelligence08Icon size={12} />
              </Box>
            )}

            <MetaDefault
              truncate
              flex="1"
              onDoubleClick={(e) => {
                e.stopPropagation();
                handleStartEdit(tab.title);
              }}
            >
              {displayTitle}
            </MetaDefault>
          </>
        )}
        {totalTabs > 1 && !isEditing && (
          <IconButton
            variant="link"
            size="xs"
            fontSize="xs"
            aria-label="Close chat tab"
            onClick={(e) => {
              e.stopPropagation();
              onCloseTab({ tabId: tab.tabId });
            }}
            color="fg.subtle"
            _hover={{
              color: 'fg.default',
            }}
            minW="auto"
            h="auto"
            p="0"
            opacity={isHovered ? 1 : 0}
            visibility={isHovered ? 'visible' : 'hidden'}
            transition="opacity 0.1s ease, visibility 0.1s ease"
          >
            <Cancel01Icon />
          </IconButton>
        )}
      </Box>
    </ChatTabContextMenu>
  );
}
